import React, { useState, useEffect, useContext } from 'react'
import { Table, Button, Popconfirm, notification, Tag } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import EscalationModal from './EscalationModal'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { GET_ESCALATION_RULES } from '../graphql/Queries'
import {
  CREATE_ESCALATION_RULE,
  UPDATE_ESCALATION_RULE,
  DELETE_ESCALATION_RULE,
} from '../graphql/Mutations'
import { AppContext } from 'AppContext'
import { canUserEdit, canUserAdd, canUserDelete } from 'utils/User'

const PAGE_SIZE = 10

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'HIGH':
      return 'red'
    case 'MEDIUM':
      return 'orange'
    case 'LOW':
      return 'green'
    default:
      return 'default'
  }
}

const EscalationsTable = () => {
  const { state: { currentUser, userPermissions, isClient } = {} } = useContext(AppContext) || {}
  const canEdit = canUserEdit(userPermissions || currentUser, isClient)
  const canAdd = canUserAdd(userPermissions || currentUser, isClient)
  const canDelete = canUserDelete(userPermissions || currentUser, isClient)

  const { data, loading, refetch } = useQuery(GET_ESCALATION_RULES)
  const [createEscalationRule] = useMutation(CREATE_ESCALATION_RULE)
  const [updateEscalationRule] = useMutation(UPDATE_ESCALATION_RULE)
  const [deleteEscalationRule] = useMutation(DELETE_ESCALATION_RULE)

  const [modalVisible, setModalVisible] = useState(false)
  const [editingEscalation, setEditingEscalation] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [escalations, setEscalations] = useState([])

  useEffect(() => {
    if (data && data.getEscalationRules) {
      setEscalations(data.getEscalationRules)
    }
  }, [data])

  const handleAdd = () => {
    if (!canAdd) return
    setEditingEscalation(null)
    setModalVisible(true)
  }

  const handleEdit = (record) => {
    if (!canEdit) return
    setEditingEscalation(record)
    setModalVisible(true)
  }

  const handleDelete = async (id) => {
    if (!canDelete) return
    try {
      await deleteEscalationRule({ variables: { id } })
      notification.success({ message: 'Escalation rule deleted' })
      refetch()
    } catch (err) {
      notification.error({ message: 'Delete failed' })
    }
  }

  const handleModalOk = async (values) => {
    if (editingEscalation && !canEdit) return
    if (!editingEscalation && !canAdd) return
    try {
      if (editingEscalation) {
        await updateEscalationRule({
          variables: { id: editingEscalation.id, data: values },
        })
        notification.success({ message: 'Escalation rule updated' })
      } else {
        await createEscalationRule({ variables: { data: values } })
        notification.success({ message: 'Escalation rule created' })
      }
      setModalVisible(false)
      refetch()
    } catch (err) {
      notification.error({ message: 'Operation failed' })
    }
  }

  const columns = [
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => (
        <Tag
          color={getPriorityColor(priority?.priorityLevel?.toUpperCase())}
          style={{ fontWeight: 500 }}
        >
          {priority?.priorityLevel}
        </Tag>
      ),
    },
    {
      title: 'Escalate To',
      dataIndex: 'escalateTo',
      key: 'escalateTo',
    },
    {
      title: 'Trigger Value (hrs)',
      dataIndex: 'triggerValue',
      key: 'triggerValue',
    },
    {
      title: 'Action Description',
      dataIndex: 'actionDescription',
      key: 'actionDescription',
    },
    ...((canEdit || canDelete)
      ? [
          {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
              <>
                {canEdit && (
                  <EditOutlined
                    style={{ marginRight: 12, color: '#1890ff' }}
                    onClick={() => handleEdit(record)}
                  />
                )}
                {canDelete && (
                  <Popconfirm
                    title='Delete?'
                    onConfirm={() => handleDelete(record.id)}
                  >
                    <DeleteOutlined style={{ color: 'red' }} />
                  </Popconfirm>
                )}
              </>
            ),
          },
        ]
      : []),
  ]

  // Pagination logic
  const paginatedData = escalations.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  )

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 18 }}>Escalation Rules</div>
        {canAdd && (
          <Button type='primary' icon={<PlusOutlined />} onClick={handleAdd}>
            Add Escalation Rule
          </Button>
        )}
      </div>
      <Table
        columns={columns}
        dataSource={paginatedData}
        rowKey='id'
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: PAGE_SIZE,
          total: escalations.length,
          onChange: setCurrentPage,
        }}
      />
      <EscalationModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleModalOk}
        editingEscalation={editingEscalation}
      />
    </div>
  )
}

export default EscalationsTable
